/**
 * @fileoverview A dialog showing just one action's form for a tenant.
 * @description Opened from a table row's actions menu (see PipelineRow): instead of the full
 * detail view with that action's panel somewhere inside it, the dialog shows only the picked
 * action's form — Edit Prospect, Edit Target, Update Stage, Schedule Follow-up, Reassign Owner,
 * Confirm Payment, Archive/Restore or Add Activity — titled with the action and the bank. Saving or
 * cancelling closes it. The permission is checked again here against freshly loaded data (see
 * resolveTenantActions), so a stale menu can never open a form the viewer may no longer use; in
 * that case the dialog says the action isn't available instead.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'

import { useSalesRoster } from '@/features/sales/hooks/useSalesRoster'
import { useTenant } from '@/features/sales/hooks/useTenant'
import { useTenantTarget } from '@/features/sales/hooks/useTenantTarget'
import { pickRelevantTarget } from '@/features/sales/lib/target-selection.utils'
import {
  classifyTenantAccess,
  resolveTenantActions,
  TENANT_ACTION_PERMISSION,
} from '@/features/sales/lib/tenant-access.utils'
import Skeleton from '@/shared/components/atoms/Skeleton'
import Modal from '@/shared/components/molecules/Modal'

import AddActivityForm from './AddActivityForm'
import ConfirmPaymentPanel from './ConfirmPaymentPanel'
import EditProspectForm from './EditProspectForm'
import EditTargetForm from './EditTargetForm'
import ReasonNotePanel from './ReasonNotePanel'
import ReassignOwnerForm from './ReassignOwnerForm'
import ScheduleFollowUpForm from './ScheduleFollowUpForm'
import UpdateSalesStageForm from './UpdateSalesStageForm'

import type { TenantAction } from '@/features/sales/lib/tenant-access.utils'
import type { TargetWithOverdue } from '@/features/sales/mocks/targets-mock-store'
import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'
import type { ReactNode } from 'react'

// Actions that work on the tenant's target, so need one to show their form.
const NEEDS_TARGET: ReadonlySet<TenantAction> = new Set([
  'editTarget',
  'updateStage',
  'scheduleFollowUp',
  'reassign',
  'confirmPayment',
])

interface ActionContext {
  tenant: Tenant
  target: TargetWithOverdue | undefined
  roster: SalesUserRecord[]
  viewerUserId: string
  viewerRole: string
  close: () => void
}

/**
 * @description Renders the one form for a target-free action: edit, archive/restore or activity.
 * @param {TenantAction} action - The action to show.
 * @param {ActionContext} context - The tenant and viewer it applies to, and the close handler.
 * @returns {ReactNode} The action's form.
 */
function renderProspectAction(action: TenantAction, context: ActionContext): ReactNode {
  const { tenant, viewerUserId, viewerRole, close } = context
  switch (action) {
    case 'edit':
      return (
        <EditProspectForm
          tenant={tenant}
          actingUserId={viewerUserId}
          actingUserRole={viewerRole}
          onUpdated={close}
          onCancel={close}
        />
      )
    case 'archive':
    case 'restore':
      return <ReasonNotePanel mode={action} tenantId={tenant.id} onDone={close} onCancel={close} />
    default:
      return (
        <AddActivityForm
          tenantId={tenant.id}
          currentUserId={viewerUserId}
          onAdded={close}
          onCancel={close}
        />
      )
  }
}

/**
 * @description Renders the one form for an action on the tenant's target.
 * @param {TenantAction} action - The action to show.
 * @param {ActionContext} context - The tenant and viewer it applies to, and the close handler.
 * @param {TargetWithOverdue} target - The tenant's relevant target.
 * @returns {ReactNode} The action's form.
 */
function renderTargetAction(
  action: TenantAction,
  context: ActionContext,
  target: TargetWithOverdue,
): ReactNode {
  const { tenant, roster, viewerUserId, viewerRole, close } = context
  const common = {
    tenantId: tenant.id,
    actingUserId: viewerUserId,
    onUpdated: close,
    onCancel: close,
  }
  switch (action) {
    case 'editTarget':
      return <EditTargetForm {...common} target={target} actingUserRole={viewerRole} />
    case 'updateStage':
      return <UpdateSalesStageForm {...common} targetId={target.id} currentStage={target.stage} />
    case 'scheduleFollowUp':
      return (
        <ScheduleFollowUpForm
          {...common}
          targetId={target.id}
          currentNextFollowUpAt={target.nextFollowUpAt}
          actingUserRole={viewerRole}
        />
      )
    case 'reassign':
      return (
        <ReassignOwnerForm
          {...common}
          targetId={target.id}
          currentOwnerId={tenant.currentOwnerId}
          roster={roster}
          actingUserRole={viewerRole}
        />
      )
    default:
      return (
        <ConfirmPaymentPanel
          tenantId={tenant.id}
          targetId={target.id}
          confirmedByUserId={viewerUserId}
          onDone={close}
          onCancel={close}
        />
      )
  }
}

/**
 * @description Renders the action's form, or a short notice when the viewer may not take it.
 * @param {TenantAction} action - The action to show.
 * @param {ActionContext} context - The tenant and viewer it applies to, and the close handler.
 * @returns {ReactNode} The dialog's body.
 */
function ActionBody({ action, context }: { action: TenantAction; context: ActionContext }) {
  const { t } = useTranslation()
  const { tenant, target, roster, viewerUserId, viewerRole } = context
  const access = classifyTenantAccess(tenant, viewerUserId, viewerRole, roster)
  const allowed = resolveTenantActions(tenant, target, access)[TENANT_ACTION_PERMISSION[action]]

  if (!allowed || (NEEDS_TARGET.has(action) && !target)) {
    return (
      <p className="text-sm text-slate" data-testid="tenant-action-unavailable">
        {t('sales.tenantDetail.actionUnavailable')}
      </p>
    )
  }
  return target && NEEDS_TARGET.has(action)
    ? renderTargetAction(action, context, target)
    : renderProspectAction(action, context)
}

interface TenantActionModalProps {
  tenantId: string | null
  action: TenantAction | null
  onClose: () => void
  viewerUserId: string
  viewerRole: string
}

/**
 * @component
 * @param {TenantActionModalProps} props - The tenant and action to show, and the viewer.
 * @param {string | null} props.tenantId - The tenant to act on, or null to keep it closed.
 * @param {TenantAction | null} props.action - The action whose form to show, or null.
 * @param {() => void} props.onClose - Called after saving, cancelling or dismissing.
 * @param {string} props.viewerUserId - The signed-in user's sales-roster id.
 * @param {string} props.viewerRole - The signed-in user's active role name.
 */
function TenantActionModal({
  tenantId,
  action,
  onClose,
  viewerUserId,
  viewerRole,
}: TenantActionModalProps) {
  const { t } = useTranslation()
  const id = tenantId ?? ''
  const tenantQuery = useTenant(id)
  const targetQuery = useTenantTarget(id)
  const rosterQuery = useSalesRoster()

  if (!tenantId || !action) return null

  const tenant = tenantQuery.data
  const title = t(`sales.tenantDetail.actions.${action}`)
  const isLoading = tenantQuery.isPending || targetQuery.isPending || rosterQuery.isPending

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      subtitle={tenant?.name}
      size="lg"
      data-testid="tenant-action-modal"
    >
      {isLoading && (
        <div className="flex flex-col gap-3" data-testid="tenant-action-loading">
          <Skeleton height={20} width="60%" />
          <Skeleton height={60} />
        </div>
      )}
      {!isLoading && !tenant && (
        <p className="text-sm text-danger" data-testid="tenant-action-load-error">
          {t('sales.activeClients.loadError')}
        </p>
      )}
      {!isLoading && tenant && (
        <ActionBody
          action={action}
          context={{
            tenant,
            target: pickRelevantTarget(targetQuery.data ?? []),
            roster: rosterQuery.data ?? [],
            viewerUserId,
            viewerRole,
            close: onClose,
          }}
        />
      )}
    </Modal>
  )
}

export default TenantActionModal
